import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  Link
} from '@mui/material';
import axios from 'axios';
import { API_ENDPOINTS } from '../config';
import { validationUtils } from '../utils/validation';
import EmailVerificationModal from '../components/EmailVerificationModal';

const Register: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const navigate = useNavigate();

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    setEmailError(validationUtils.getEmailError(value));
  };

  const handleEmailBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmailError(validationUtils.getEmailError(value));
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPassword(value);
    setPasswordError(validationUtils.getPasswordError(value));
    
    // Check confirm password match
    if (confirmPassword && value !== confirmPassword) {
      setConfirmPasswordError('Mật khẩu xác nhận không khớp');
    } else {
      setConfirmPasswordError('');
    }
  };

  const handlePasswordBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPasswordError(validationUtils.getPasswordError(value));
  };

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setConfirmPassword(value);
    
    if (password && value !== password) {
      setConfirmPasswordError('Mật khẩu xác nhận không khớp');
    } else {
      setConfirmPasswordError('');
    }
  };

  const handleConfirmPasswordBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value;
    
    if (password && value !== password) {
      setConfirmPasswordError('Mật khẩu xác nhận không khớp');
    } else {
      setConfirmPasswordError('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate all inputs
    const emailErrorMsg = validationUtils.getEmailError(email);
    const passwordErrorMsg = validationUtils.getPasswordError(password);
    let confirmPasswordErrorMsg = '';

    if (!confirmPassword) {
      confirmPasswordErrorMsg = 'Vui lòng xác nhận mật khẩu';
    } else if (password !== confirmPassword) {
      confirmPasswordErrorMsg = 'Mật khẩu xác nhận không khớp';
    }

    setEmailError(emailErrorMsg);
    setPasswordError(passwordErrorMsg);
    setConfirmPasswordError(confirmPasswordErrorMsg);

    if (emailErrorMsg || passwordErrorMsg || confirmPasswordErrorMsg) {
      return;
    }

    setLoading(true);

    try {
      console.log('Submitting registration for:', email); // Debug log
      await axios.post(API_ENDPOINTS.AUTH.REGISTER, {
        email: email.trim(),
        password: password
      });

      console.log('Registration successful, showing modal'); // Debug log
      // Show verification modal instead of redirecting
      setShowVerificationModal(true);
    } catch (err: any) {
      console.error('Registration error:', err); // Debug log
      setError(err.response?.data?.message || 'Đăng ký thất bại. Vui lòng thử lại.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerificationModalClose = () => {
    console.log('Closing verification modal'); // Debug log
    setShowVerificationModal(false);
    // Redirect to login after closing modal
    navigate('/login', { 
      state: { 
        message: 'Đăng ký thành công! Vui lòng kiểm tra email để xác thực tài khoản.' 
      }
    });
  };

  const isFormValid = !emailError && !passwordError && !confirmPasswordError && 
                     email.trim() && password.trim() && confirmPassword.trim();

  console.log('Register component render - showVerificationModal:', showVerificationModal); // Debug log

  return (
    <>
      <Container maxWidth="sm">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper
            elevation={3}
            sx={{
              padding: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Typography component="h1" variant="h5" gutterBottom>
              Đăng ký tài khoản
            </Typography>

            {error && (
              <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email"
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={handleEmailChange}
                onBlur={handleEmailBlur}
                error={!!emailError}
                helperText={emailError}
                type="email"
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Mật khẩu"
                type="password"
                id="password"
                autoComplete="new-password"
                value={password}
                onChange={handlePasswordChange}
                onBlur={handlePasswordBlur}
                error={!!passwordError}
                helperText={passwordError}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="confirmPassword"
                label="Xác nhận mật khẩu"
                type="password"
                id="confirmPassword"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={handleConfirmPasswordChange}
                onBlur={handleConfirmPasswordBlur}
                error={!!confirmPasswordError}
                helperText={confirmPasswordError}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={loading || !isFormValid}
              >
                {loading ? 'Đang đăng ký...' : 'Đăng ký'}
              </Button>
              <Box sx={{ textAlign: 'center' }}>
                <Link component={RouterLink} to="/login" variant="body2">
                  Đã có tài khoản? Đăng nhập
                </Link>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Container>

      <EmailVerificationModal
        isOpen={showVerificationModal}
        onClose={handleVerificationModalClose}
        email={email}
      />
    </>
  );
};

export default Register; 